<script setup lang="ts">
import { ref, watch } from 'vue'
import { UiButton, UiIconButton, UiInputField, UiText, type UiIconProps } from '../ui'

const props = withDefaults(defineProps<{
  sidebarOpen: boolean
  title: string
  subtitle?: string
  urlValue: string
  urlError?: string
  urlPlaceholder?: string
  urlDisabled?: boolean
  showUrl?: boolean
  statusLabel?: string
  canSave: boolean
  isSaving?: boolean
  showBack?: boolean
  showHistory?: boolean
  saveLabel?: string
  backLabel?: string
  sidebarLabelOpen?: string
  sidebarLabelClosed?: string
  urlIcon?: UiIconProps['name']
}>(), {
  subtitle: '',
  urlError: '',
  urlPlaceholder: '/',
  urlDisabled: false,
  showUrl: true,
  statusLabel: '',
  isSaving: false,
  showBack: true,
  showHistory: false,
  saveLabel: 'Save',
  backLabel: 'Back',
  sidebarLabelOpen: 'Close sidebar',
  sidebarLabelClosed: 'Open sidebar',
  urlIcon: 'link-external',
})

const emit = defineEmits<{
  (event: 'back'): void
  (event: 'toggle-sidebar'): void
  (event: 'submit-url', value: string): void
  (event: 'open-history'): void
  (event: 'save'): void
}>()

const localUrl = ref(props.urlValue)

watch(() => props.urlValue, (value) => {
  localUrl.value = value
})

function commitUrl() {
  if (localUrl.value !== props.urlValue) {
    emit('submit-url', localUrl.value)
  }
}
</script>

<template>
  <header class="flex h-16 shrink-0 items-center gap-3 border-b border-se-gray-300 bg-white px-4">
    <UiIconButton
      v-if="props.showBack"
      icon="arrow-narrow-left"
      :aria-label="props.backLabel"
      variant="ghost"
      class="h-9 w-9"
      @click="emit('back')"
    />

    <UiIconButton
      icon="sidebar-01"
      :aria-label="props.sidebarOpen ? props.sidebarLabelOpen : props.sidebarLabelClosed"
      :aria-pressed="props.sidebarOpen"
      variant="outline"
      :class="props.sidebarOpen ? 'h-9 w-9 bg-se-gray-100' : 'h-9 w-9'"
      @click="emit('toggle-sidebar')"
    />

    <div v-if="props.title || props.subtitle" class="hidden min-w-0 sm:block">
      <UiText as="h1" variant="h5" class="truncate font-semibold" :text="props.title" />
      <UiText
        v-if="props.subtitle"
        as="p"
        variant="caption-normal"
        class="truncate text-se-gray-700"
        :text="props.subtitle"
      />
    </div>

    <form
      v-if="props.showUrl"
      class="min-w-0 flex-1"
      @submit.prevent="commitUrl"
      @keydown.enter.prevent="commitUrl"
      @focusout.capture="commitUrl"
    >
      <UiInputField
        v-model="localUrl"
        :icon="props.urlIcon"
        name="admin-editor-url"
        :placeholder="props.urlPlaceholder"
        :invalid="Boolean(props.urlError)"
        :error="props.urlError"
        :disabled="props.urlDisabled"
      />
    </form>

    <div v-else class="min-w-0 flex-1" />

    <div class="hidden items-center gap-2 sm:flex">
      <slot name="status">
        <div v-if="props.statusLabel" class="rounded-full bg-se-gray-100 px-2.5 py-1">
          <UiText as="span" variant="caption-normal" class="font-semibold text-se-gray-700" :text="props.statusLabel" />
        </div>
      </slot>
      <UiButton
        v-if="props.showHistory"
        variant="outline"
        size="sm"
        icon="clock"
        label="History"
        @click="emit('open-history')"
      />
      <UiButton
        icon="check"
        :label="props.saveLabel"
        size="sm"
        :disabled="!props.canSave || props.isSaving"
        @click="emit('save')"
      />
    </div>
  </header>
</template>
